import {
  type MapDefinition,
  type Region,
  type Territory,
  regionId,
  territoryId,
} from './schema.js';

const NORTHWILDS = regionId('northwilds');
const SPINE = regionId('spine');
const TIDEGLASS = regionId('tideglass');

// Layout: three landmasses arranged on a NW-to-SE diagonal across the
// map zone. Margins are roughly equal on all four sides (~30-40 px),
// and the gaps between continents are also ~40 px ("ocean"), giving
// the map a balanced, centered feel.
//
//   ┌──┬──┬──┐                                            ← Northwilds (6 tiles)
//   │NW│N │NE│
//   ├──┼──┘
//   │W │M │
//   ├──┘
//   │S │
//   └──┘
//                  ┌──┬──┐                                ← Spine (2×2 square)
//                  │NW│NE│                                  in the middle
//                  ├──┼──┤
//                  │SW│SE│
//                  └──┴──┘
//                                  ┌──┬──┐                ← Tideglass (6 tiles)
//                                  │NW│NE│                  bottom-right;
//                                  ├──┼──┤                  ~32 px above the
//                                  │W │E │                  dice tray.
//                                  ├──┼──┤
//                                  │SW│SE│
//                                  └──┴──┘
//
// Cross-region bridges (2 total):
//   northwilds_ne ↔ spine_nw      (Northwilds → Spine, top-right entry)
//   spine_se ↔ tideglass_nw       (Spine → Tideglass, bottom-right exit)
//
// Symmetric chokepoint: 1 entry on each side. The Spine is small and
// efficient (4 tiles, +3 bonus = 0.75 bonus/tile), but losing either
// of its two corner connection tiles severs the matching landmass.

const T = {
  // Northwilds — top-left landmass
  northwildsNW: territoryId('northwilds_nw'),
  northwildsN: territoryId('northwilds_n'),
  northwildsNE: territoryId('northwilds_ne'),
  northwildsW: territoryId('northwilds_w'),
  northwildsM: territoryId('northwilds_mid'),
  northwildsS: territoryId('northwilds_s'),
  // Spine — 2×2 square in the middle
  spineNW: territoryId('spine_nw'),
  spineNE: territoryId('spine_ne'),
  spineSW: territoryId('spine_sw'),
  spineSE: territoryId('spine_se'),
  // Tideglass — bottom-right landmass
  tideglassNW: territoryId('tideglass_nw'),
  tideglassNE: territoryId('tideglass_ne'),
  tideglassW: territoryId('tideglass_w'),
  tideglassE: territoryId('tideglass_e'),
  tideglassSW: territoryId('tideglass_sw'),
  tideglassSE: territoryId('tideglass_se'),
} as const;

// Bonuses: both landmasses pay +3 (6 tiles each). The Spine pays +3
// despite only having 4 tiles — its 0.75 bonus/tile makes it the
// most reinforcement-efficient region, but the chokepoint shape
// means holding all 4 is genuinely contested. Total pool = 9.
const regions: readonly Region[] = [
  { id: NORTHWILDS, name: 'Northwilds', territoryBonus: 3 },
  { id: SPINE, name: 'The Spine', territoryBonus: 3 },
  { id: TIDEGLASS, name: 'Tideglass', territoryBonus: 3 },
];

const territories: readonly Territory[] = [
  // --- Northwilds (6 tiles, NW corner) ---
  // 3-wide top row, 2-wide mid row, 1 tile in left column row 3.
  // Left edge sits ~40 px from canvas left.
  {
    id: T.northwildsNW,
    name: 'Northwilds NW',
    region: NORTHWILDS,
    position: { x: 80, y: 320 },
    adjacent: [T.northwildsN, T.northwildsW],
  },
  {
    id: T.northwildsN,
    name: 'Northwilds N',
    region: NORTHWILDS,
    position: { x: 160, y: 320 },
    adjacent: [T.northwildsNW, T.northwildsNE, T.northwildsM],
  },
  {
    id: T.northwildsNE,
    name: 'Northwilds NE',
    region: NORTHWILDS,
    position: { x: 240, y: 320 },
    adjacent: [T.northwildsN, T.spineNW],
  },
  {
    id: T.northwildsW,
    name: 'Northwilds W',
    region: NORTHWILDS,
    position: { x: 80, y: 400 },
    adjacent: [T.northwildsNW, T.northwildsM, T.northwildsS],
  },
  {
    id: T.northwildsM,
    name: 'Northwilds Mid',
    region: NORTHWILDS,
    position: { x: 160, y: 400 },
    adjacent: [T.northwildsN, T.northwildsW],
  },
  {
    id: T.northwildsS,
    name: 'Northwilds S',
    region: NORTHWILDS,
    position: { x: 80, y: 480 },
    adjacent: [T.northwildsW],
  },

  // --- The Spine (2×2 square in the middle) ---
  // 40-px ocean gap from Northwilds on the left and Tideglass on the
  // right (and vertically). NW is the only entry from Northwilds; SE
  // is the only exit toward Tideglass.
  {
    id: T.spineNW,
    name: 'Spine NW',
    region: SPINE,
    position: { x: 360, y: 400 },
    adjacent: [T.spineNE, T.spineSW, T.northwildsNE],
  },
  {
    id: T.spineNE,
    name: 'Spine NE',
    region: SPINE,
    position: { x: 440, y: 400 },
    adjacent: [T.spineNW, T.spineSE],
  },
  {
    id: T.spineSW,
    name: 'Spine SW',
    region: SPINE,
    position: { x: 360, y: 480 },
    adjacent: [T.spineNW, T.spineSE],
  },
  {
    id: T.spineSE,
    name: 'Spine SE',
    region: SPINE,
    position: { x: 440, y: 480 },
    adjacent: [T.spineNE, T.spineSW, T.tideglassNW],
  },

  // --- Tideglass (6 tiles, 2 cols × 3 rows, SE corner) ---
  // Bottom row at y=760 → bottom edge at y=800, leaving ~32 px between
  // the landmass and the dice tray (which starts at y=832). Right
  // edge sits ~40 px from canvas right.
  {
    id: T.tideglassNW,
    name: 'Tideglass NW',
    region: TIDEGLASS,
    position: { x: 560, y: 600 },
    adjacent: [T.tideglassNE, T.tideglassW, T.spineSE],
  },
  {
    id: T.tideglassNE,
    name: 'Tideglass NE',
    region: TIDEGLASS,
    position: { x: 640, y: 600 },
    adjacent: [T.tideglassNW, T.tideglassE],
  },
  {
    id: T.tideglassW,
    name: 'Tideglass W',
    region: TIDEGLASS,
    position: { x: 560, y: 680 },
    adjacent: [T.tideglassNW, T.tideglassE, T.tideglassSW],
  },
  {
    id: T.tideglassE,
    name: 'Tideglass E',
    region: TIDEGLASS,
    position: { x: 640, y: 680 },
    adjacent: [T.tideglassNE, T.tideglassW, T.tideglassSE],
  },
  {
    id: T.tideglassSW,
    name: 'Tideglass SW',
    region: TIDEGLASS,
    position: { x: 560, y: 760 },
    adjacent: [T.tideglassW, T.tideglassSE],
  },
  {
    id: T.tideglassSE,
    name: 'Tideglass SE',
    region: TIDEGLASS,
    position: { x: 640, y: 760 },
    adjacent: [T.tideglassE, T.tideglassSW],
  },
];

export const slashMap: MapDefinition = {
  id: 'slash',
  name: 'Slash',
  regions,
  territories,
};
